// ============================================================
// Daily Meetings — data shapes, API wrapper, and pure helpers.
//
// PHASE_2D: the Daily Meetings mini portal is a REAL persisted workflow
// backed by sq_daily_meetings via /api/square/command-center/daily-meetings.
// This module NEVER fabricates a meeting — the list comes only from the
// API, and empty/auth-required/error states are surfaced honestly.
// ============================================================

import { apiRequest } from '../../../../api/client'

export const MEETING_TYPES = ['daily', 'weekly', 'adhoc', 'retro', 'planning'] as const
export type MeetingType = (typeof MEETING_TYPES)[number]

export type ActionItemStatus = 'open' | 'done' | 'blocked'

export interface DailyMeetingActionItem { text: string; owner?: string; status: ActionItemStatus }
export interface DailyMeetingBlocker { text: string; severity?: 'low' | 'medium' | 'high' }
export interface DailyMeetingNote { text: string }
export interface DailyMeetingLink { label: string; url?: string }

export interface DailyMeetingRecord {
  id: string
  meeting_date: string
  meeting_title: string
  meeting_type: MeetingType
  attendees: string[]
  lanes_discussed: string[]
  last_24h_summary: string | null
  next_24h_plan: string | null
  blockers: DailyMeetingBlocker[]
  decisions: DailyMeetingNote[]
  action_items: DailyMeetingActionItem[]
  readiness_changes: DailyMeetingNote[]
  links_or_receipts: DailyMeetingLink[]
  follow_up_needed: boolean
  created_by: string
  created_at: string
  updated_at: string
}

/** The mutable fields accepted on create/update. */
export interface DailyMeetingDraft {
  meeting_title: string
  meeting_type: MeetingType
  meeting_date: string
  attendees?: string[]
  lanes_discussed?: string[]
  last_24h_summary?: string | null
  next_24h_plan?: string | null
  blockers?: DailyMeetingBlocker[]
  decisions?: DailyMeetingNote[]
  action_items?: DailyMeetingActionItem[]
  readiness_changes?: DailyMeetingNote[]
  links_or_receipts?: DailyMeetingLink[]
  follow_up_needed?: boolean
}

// ---- pure helpers (no I/O, unit-testable) --------------------------------

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []
}
function asNotes(v: unknown): DailyMeetingNote[] {
  return Array.isArray(v) ? v.map((x) => ({ text: String((x as any)?.text ?? x ?? '') })).filter((n) => n.text) : []
}
function asBlockers(v: unknown): DailyMeetingBlocker[] {
  return Array.isArray(v)
    ? v.map((x) => ({ text: String((x as any)?.text ?? x ?? ''), severity: (x as any)?.severity })).filter((b) => b.text)
    : []
}
function asActionItems(v: unknown): DailyMeetingActionItem[] {
  const ok: ActionItemStatus[] = ['open', 'done', 'blocked']
  return Array.isArray(v)
    ? v
        .map((x) => ({
          text: String((x as any)?.text ?? x ?? ''),
          owner: typeof (x as any)?.owner === 'string' ? (x as any).owner : undefined,
          status: ok.includes((x as any)?.status) ? ((x as any).status as ActionItemStatus) : 'open',
        }))
        .filter((a) => a.text)
    : []
}
function asLinks(v: unknown): DailyMeetingLink[] {
  return Array.isArray(v)
    ? v.map((x) => ({ label: String((x as any)?.label ?? x ?? ''), url: typeof (x as any)?.url === 'string' ? (x as any).url : undefined })).filter((l) => l.label)
    : []
}

/** Coerce an API row into a fully-typed record with safe defaults. */
export function normalizeMeeting(row: any): DailyMeetingRecord {
  return {
    id: String(row?.id ?? ''),
    meeting_date: String(row?.meeting_date ?? ''),
    meeting_title: String(row?.meeting_title ?? 'Untitled meeting'),
    meeting_type: (MEETING_TYPES as readonly string[]).includes(row?.meeting_type) ? row.meeting_type : 'daily',
    attendees: asStringArray(row?.attendees),
    lanes_discussed: asStringArray(row?.lanes_discussed),
    last_24h_summary: typeof row?.last_24h_summary === 'string' ? row.last_24h_summary : null,
    next_24h_plan: typeof row?.next_24h_plan === 'string' ? row.next_24h_plan : null,
    blockers: asBlockers(row?.blockers),
    decisions: asNotes(row?.decisions),
    action_items: asActionItems(row?.action_items),
    readiness_changes: asNotes(row?.readiness_changes),
    links_or_receipts: asLinks(row?.links_or_receipts),
    follow_up_needed: row?.follow_up_needed === true,
    created_by: String(row?.created_by ?? ''),
    created_at: String(row?.created_at ?? ''),
    updated_at: String(row?.updated_at ?? ''),
  }
}

/** A blank draft for the create form — today's date, sensible defaults. */
export function emptyMeetingDraft(today: string): DailyMeetingDraft {
  return { meeting_title: '', meeting_type: 'daily', meeting_date: today }
}

/** Derived counts for a meeting card/summary — never fabricated. */
export function summarizeMeeting(m: DailyMeetingRecord) {
  return {
    openActions: m.action_items.filter((a) => a.status === 'open').length,
    blockedActions: m.action_items.filter((a) => a.status === 'blocked').length,
    doneActions: m.action_items.filter((a) => a.status === 'done').length,
    blockers: m.blockers.length,
    decisions: m.decisions.length,
  }
}

/** Cycle an action item's status open → done → blocked → open. */
export function nextActionStatus(s: ActionItemStatus): ActionItemStatus {
  return s === 'open' ? 'done' : s === 'done' ? 'blocked' : 'open'
}

// ---- API wrapper (browser-only; apiRequest attaches the bearer token) -----

export async function listDailyMeetings(signal?: AbortSignal): Promise<DailyMeetingRecord[]> {
  const res = await apiRequest<{ ok: true; meetings: unknown[] }>('/api/square/command-center/daily-meetings', { method: 'GET', signal })
  return (res.meetings || []).map(normalizeMeeting)
}

export async function createDailyMeeting(draft: DailyMeetingDraft): Promise<DailyMeetingRecord> {
  const res = await apiRequest<{ ok: true; meeting: unknown }>('/api/square/command-center/daily-meetings', { method: 'POST', body: draft })
  return normalizeMeeting(res.meeting)
}

export async function updateDailyMeeting(id: string, patch: Partial<DailyMeetingDraft>): Promise<DailyMeetingRecord> {
  const res = await apiRequest<{ ok: true; meeting: unknown }>('/api/square/command-center/daily-meetings', { method: 'POST', body: { action: 'update', id, ...patch } })
  return normalizeMeeting(res.meeting)
}
